import { useEffect, useState } from "react";
import axios from "axios";
import { Send, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "./ui";
import QuoteDocument from "./QuoteDocument";
import QuoteEditForm from "./QuoteEditForm";
import QuoteOrganizationPanel from "./QuoteOrganizationPanel";
import QuoteTimeline from "./QuoteTimeline";
import QuoteSendConfirmModal from "./QuoteSendConfirmModal";
import DeleteQuoteConfirmModal from "./DeleteQuoteConfirmModal";
import { computeQuoteTotals } from "@/lib/money";

const buildForm = (quote) => ({
  company: quote.company || "",
  contact_name: quote.contact_name || "",
  contact_email: quote.contact_email || "",
  industry: quote.industry || "",
  billing_frequency: quote.billing_frequency || "Monthly",
  valid_until: quote.valid_until ? String(quote.valid_until).slice(0, 10) : "",
  payment_terms: quote.payment_terms || "",
  notes: quote.notes || "",
  terms: quote.terms || "",
  items: quote.items || [],
  discount: quote.discount || { type: "none", percent: null, amount_cents: null, applies_to: "both" },
  tax: quote.tax || { enabled: false, rate_percent: 0 },
});

export default function QuoteDrawer({ quoteId, open, onClose, onUpdated }) {
  const { API } = useAuth();
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [orgCreating, setOrgCreating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!open || !quoteId) {
      setQuote(null);
      setForm(null);
      setPreviewMode(false);
      return;
    }
    setLoading(true);
    setError("");
    axios
      .get(`${API}/admin/quotes/${quoteId}`, { withCredentials: true })
      .then(({ data }) => {
        setQuote(data.quote);
        setForm(buildForm(data.quote));
        setPreviewMode(data.quote.status !== "Draft");
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [API, open, quoteId]);

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const totals = form ? computeQuoteTotals(form.items, form.discount, form.tax) : null;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/admin/quotes/${quoteId}`, form, { withCredentials: true });
      setQuote(data.quote);
      setForm(buildForm(data.quote));
      onUpdated?.();
      return data.quote;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const reviewAndSend = async () => {
    const saved = await save();
    if (saved) setPreviewMode(true);
  };

  const sendQuote = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/quotes/${quoteId}/send`, {}, { withCredentials: true });
      setQuote(data.quote);
      setSendConfirmOpen(false);
      onUpdated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createOrganization = async () => {
    setOrgCreating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/quotes/${quoteId}/create-organization`, {}, { withCredentials: true });
      setQuote(data.quote);
      onUpdated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOrgCreating(false);
    }
  };

  const deleteQuote = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, { withCredentials: true });
      setDeleteConfirmOpen(false);
      onUpdated?.();
      onClose();
    } catch (err) {
      setDeleteError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const isDraft = quote?.status === "Draft";

  return (
    <DetailDrawer open={open} onClose={onClose} title={quote?.company || "Quote"} subtitle={quote?.quote_id} testId="quote-drawer" wide>
      {loading || !quote || !form ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="quote-drawer-loading">
          Loading quote…
        </div>
      ) : (
        <div className="space-y-6">
          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="quote-drawer-error">{error}</p>}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              data-testid="quote-delete-btn"
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              <Trash2 size={13} /> Delete Quote
            </button>
          </div>

          {isDraft && !previewMode ? (
            <QuoteEditForm form={form} updateForm={updateForm} totals={totals} saving={saving} onSave={save} onReviewAndSend={reviewAndSend} />
          ) : (
            <div className="space-y-6" data-testid="quote-preview">
              <QuoteDocument quote={isDraft ? { ...quote, ...form, totals } : quote} mode="admin" />

              {isDraft && (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setPreviewMode(false)}
                    data-testid="quote-back-to-edit-btn"
                    className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    Back to edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendConfirmOpen(true)}
                    data-testid="quote-open-send-confirm-btn"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700"
                  >
                    <Send size={16} /> Send Quote
                  </button>
                </div>
              )}

              <QuoteOrganizationPanel
                quote={quote}
                orgCreating={orgCreating}
                onCreateOrganization={createOrganization}
                onAssigned={(data) => {
                  setQuote(data.quote);
                  onUpdated?.();
                }}
              />
            </div>
          )}

          <QuoteTimeline events={quote.quote_events} />
        </div>
      )}

      <QuoteSendConfirmModal open={sendConfirmOpen} quote={quote} saving={saving} onConfirm={sendQuote} onCancel={() => setSendConfirmOpen(false)} />
      <DeleteQuoteConfirmModal
        open={deleteConfirmOpen}
        quote={quote}
        saving={deleting}
        error={deleteError}
        onConfirm={deleteQuote}
        onCancel={() => { setDeleteConfirmOpen(false); setDeleteError(""); }}
      />
    </DetailDrawer>
  );
}
